import { lazy, useEffect, useState } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GetPlayerProfileResponse } from 'services/players';

import useBrowsing from 'hooks/use-browsing';

import { Tabs } from 'core/generic/components/general/tabs';

import { Bio } from '@players/components/bio/bio';
import { Honours } from '@players/components/honours/honours';
import InfoAndSocialsSection, {
	PlayerSocial,
} from '@players/components/info-and-socials-section/info-and-socials-section';
import { NewsList } from '@players/components/news-list/news-list';
import { OtherPlayersSection } from '@players/components/other-players-section/other-players-section';
import { getHeroPlayerProps } from '@players/helpers/get-hero-player-props';

import styles from './profile-layout.module.scss';

const HeroPlayer = lazy(() => import('core/players/components/hero-player'));

type ProfileLayoutProps = {
	data?: GetPlayerProfileResponse;
};

const getPlayerSocials = (data: GetPlayerProfileResponse): PlayerSocial[] => {
	return [
		data.twitterUsername
			? {
					type: 'Twitter/X',
					username: data.twitterUsername,
					hide: data.hideTwitter,
			  }
			: null,
		data.instagramUsername
			? {
					type: 'Instagram',
					username: data.instagramUsername,
					hide: data.hideInstagram,
			  }
			: null,
		data.twitchUsername
			? { type: 'Twitch', username: data.twitchUsername }
			: null,
		data.tiktokUsername
			? { type: 'TikTok', username: data.tiktokUsername }
			: null,
	].filter(Boolean) as PlayerSocial[];
};

export const ProfileLayout = ({ data }: ProfileLayoutProps) => {
	const router = useBrowsing();
	const [activeTab, setActiveTab] = useState(0);
	const [infoSectionHeight, setInfoSectionHeight] = useState<
		number | undefined
	>();
	const infoSectionRef = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const firstTeamLayout =
		(data?.isMensTeam || data?.isWomensTeam || data?.team === 'esports') &&
		!data?.isManager;

	useEffect(() => {
		if (!router.isReady) return;

		const pathSegments = router.asPath
			.split('?')[0]
			.split('/')
			.filter(Boolean);

		if (pathSegments.includes('news')) {
			setActiveTab(1);
		} else {
			setActiveTab(0);
		}
	}, [router.asPath, router.isReady]);

	// Measure InfoAndSocialsSection height
	useEffect(() => {
		const measure = () => {
			if (infoSectionRef.current) {
				setInfoSectionHeight(infoSectionRef.current.clientHeight);
			}
		};

		const timeout = setTimeout(measure, 300);
		window.addEventListener('resize', measure);

		return () => {
			clearTimeout(timeout);
			window.removeEventListener('resize', measure);
		};
	}, [data]);

	const handleTabChange = (index: number) => {
		if (typeof window !== 'undefined') {
			const currentPath = window.location.pathname.replace('/news', '');
			if (index === 1) {
				window.history.replaceState(null, '', `${currentPath}/news`);
			} else {
				window.history.replaceState(null, '', `${currentPath}`);
			}
			setActiveTab(index);
		}
	};

	const socials = data ? getPlayerSocials(data) : [];

	return (
		<div className={styles['profile-page']}>
			{data && (
				<>
					<HeroPlayer {...getHeroPlayerProps(data)} />
					<Tabs activeItem={activeTab}>
						{firstTeamLayout && (
							<Tabs.TabList
								onChange={handleTabChange}
								tabType="Underlined"
								className={`container ${styles['tabs-wrapper']}`}
							>
								<Tabs.Tab
									index={0}
									label={t(
										'players_profile.sub_header:profile_label',
										'Profile'
									)}
								></Tabs.Tab>
								{data.news.itemCount > 0 && (
									<Tabs.Tab
										index={1}
										label={t(
											'players_profile.news_section_title',
											'News & videos'
										)}
									></Tabs.Tab>
								)}
							</Tabs.TabList>
						)}
						<Tabs.Panel index={0}>
							<div className={styles['tab-panel-wrapper']}>
								<div className="container">
									<div
										className={`row ${styles['profile-content']}`}
									>
										<div
											className={`col-12 col-lg-7 ${styles['profile-content__bio-section']}`}
										>
											<Bio
												content={data.bio}
												infoSectionHeight={
													infoSectionHeight
												}
											/>
										</div>
										<div
											className={`col-12 col-lg-3 col-offset-lg-9 ${styles['profile-content__info-socials']}`}
										>
											<InfoAndSocialsSection
												ref={infoSectionRef}
												dateOfBirth={data.dateOfBirth}
												birthCity={data.birthCity}
												birthCountry={data.birthCountry}
												dateJoined={data.dateJoined}
												socials={socials}
											/>
										</div>
										{data.awards.length > 0 && (
											<div
												className={`col-12 col-lg-7 ${styles['profile-content__honours']}`}
											>
												<Honours
													awards={data.awards}
													name={data.name}
												/>
											</div>
										)}
									</div>
								</div>
							</div>
						</Tabs.Panel>
						{data.news.itemCount > 0 && firstTeamLayout && (
							<Tabs.Panel index={1}>
								<div className={styles['tab-panel-wrapper']}>
									<NewsList
										firstName={data.firstName}
										lastName={data.lastName}
										name={data.name}
										news={data.news}
									/>
								</div>
							</Tabs.Panel>
						)}
					</Tabs>
					{data.playersInPosition?.length > 0 &&
						(data.isMensTeam ||
							data.isWomensTeam ||
							data.team === 'esports') && (
							<OtherPlayersSection
								position={data.positionKey}
								isEsportsTeam={data.team === 'esports'}
								players={data.playersInPosition}
								backgroundImage={
									data.isManager
										? data.teamPlayerImages
												?.managerCardImage
										: data.teamPlayerImages
												?.playerCardImage ||
										  data.playerCardImage
								}
							/>
						)}
				</>
			)}
		</div>
	);
};
